/**
 * Singleton fee-payer resolver. Replaces call-site `useRelayConfig`.
 *
 * Relocated from frontend/src/lib/tx-engine/relay/fee-payer.ts as part of the
 * auth boundary migration (spec 009 FILE 14 Group E).
 *
 * The frontend version called the v1 `@morfi/backend-sdk` `getRelayConfig`.
 * The v2 SDK MUST NOT depend on v1 — the relay config route
 * (`GET /v1/config/relay`, a public endpoint) is fetched directly via the
 * universal `fetch`; the SDK's installed fetch shim rewrites the relative
 * path against the API base URL.
 *
 * Platform-neutral: `fetch` is universal. Safe in src/auth/ core.
 */

import type { TxEngineLogger } from '../config.js';

/** One signed attempt's compute-unit price and relay-paid Sender tip. */
export interface RelayLandingStep {
  percentile: number;
  priorityFeeMicroLamports: number;
  tipLamports: number;
}

/** Mainnet Helius Sender policy; the relay rejects anything above the caps. */
export interface RelayLandingPolicy {
  maxPriorityFeeLamports: number;
  tipAccounts: string[];
  steps: RelayLandingStep[];
}

export interface RelayConfig {
  feePayerPublicKey: string;
  priorityFeeMicroLamports: number;
  /** Null on devnet, or when the backend sent an unusable policy. */
  landing: RelayLandingPolicy | null;
}

/** The fee payer is stable, but the landing ladder follows the network, so it is re-read. */
const RELAY_CONFIG_TTL_MS = 10_000;

let cachedRelayConfigPromise: Promise<RelayConfig> | null = null;
let cachedRelayConfigAtMs = 0;

/** Backend `{ data, requestId }` envelope for the relay config route. */
interface RelayConfigEnvelope {
  data?: {
    feePayerPublicKey?: string;
    priorityFeeMicroLamports?: number;
    landing?: unknown;
  };
}

async function fetchRelayConfig(logger: TxEngineLogger): Promise<RelayConfig> {
  const response = await fetch('/v1/config/relay', {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Relay config HTTP ${response.status}`);
  }
  const envelope = (await response.json()) as RelayConfigEnvelope;
  const feePayer = envelope.data?.feePayerPublicKey;
  if (!feePayer) {
    throw new Error('Relay config missing feePayerPublicKey');
  }

  const configuredPriorityFee = envelope.data?.priorityFeeMicroLamports;
  const priorityFeeMicroLamports =
    typeof configuredPriorityFee === 'number' &&
    Number.isSafeInteger(configuredPriorityFee) &&
    configuredPriorityFee >= 0
      ? configuredPriorityFee
      : 0;
  if (priorityFeeMicroLamports !== configuredPriorityFee) {
    logger.warn('tx.relay_priority_fee.config_fallback', { fallbackMicroLamports: '0' });
  }

  const landing = parseLandingPolicy(envelope.data?.landing);
  if (landing === null && envelope.data?.landing !== null && envelope.data?.landing !== undefined) {
    logger.warn('tx.relay_landing.config_invalid', {});
  }

  return { feePayerPublicKey: feePayer, priorityFeeMicroLamports, landing };
}

/**
 * Resolves the relay fee policy, re-reading it after 10 s. Concurrent calls
 * share the same in-flight promise. On rejection the cache busts so a
 * subsequent call can retry. Per data-model.md::RelayFeePayerCache.
 */
export async function resolveRelayConfig(logger: TxEngineLogger): Promise<RelayConfig> {
  if (
    cachedRelayConfigPromise === null ||
    Date.now() - cachedRelayConfigAtMs >= RELAY_CONFIG_TTL_MS
  ) {
    cachedRelayConfigAtMs = Date.now();
    cachedRelayConfigPromise = fetchRelayConfig(logger);
    cachedRelayConfigPromise.catch(() => {
      cachedRelayConfigPromise = null;
    });
  }
  return cachedRelayConfigPromise;
}

export async function resolveRelayFeePayer(logger: TxEngineLogger): Promise<string> {
  return (await resolveRelayConfig(logger)).feePayerPublicKey;
}

/**
 * Clear the fee-payer cache. Called from the network-toggle path so a user
 * flipping devnet ↔ mainnet picks up the new-network's relay fee-payer on
 * the next submission.
 */
export function clearRelayFeePayerCache(): void {
  cachedRelayConfigPromise = null;
}

/** The policy, or null when any field is missing or not a non-negative safe integer. */
function parseLandingPolicy(value: unknown): RelayLandingPolicy | null {
  if (typeof value !== 'object' || value === null) return null;
  const policy = value as Partial<Record<keyof RelayLandingPolicy, unknown>>;
  const steps = Array.isArray(policy.steps) ? policy.steps.map(parseStep) : [];
  const tipAccounts = Array.isArray(policy.tipAccounts)
    ? policy.tipAccounts.filter((account): account is string => typeof account === 'string')
    : [];
  if (
    !isLamports(policy.maxPriorityFeeLamports) ||
    tipAccounts.length === 0 ||
    steps.length === 0 ||
    steps.some((step) => step === null)
  ) {
    return null;
  }
  return {
    maxPriorityFeeLamports: policy.maxPriorityFeeLamports,
    tipAccounts,
    steps: steps.filter((step): step is RelayLandingStep => step !== null),
  };
}

function parseStep(value: unknown): RelayLandingStep | null {
  if (typeof value !== 'object' || value === null) return null;
  const step = value as Partial<Record<keyof RelayLandingStep, unknown>>;
  if (
    !isLamports(step.percentile) ||
    !isLamports(step.priorityFeeMicroLamports) ||
    !isLamports(step.tipLamports)
  ) {
    return null;
  }
  return {
    percentile: step.percentile,
    priorityFeeMicroLamports: step.priorityFeeMicroLamports,
    tipLamports: step.tipLamports,
  };
}

function isLamports(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}
